import { useCallback, useEffect, useState } from "react";
import { GetStaticPaths, GetStaticProps } from "next";
import {
  Story as StoryType,
  StoryImage,
} from "@dreambigger/shared/src/types/story";
import { fetcher } from "@dreambigger/shared/src/api/api";
import { OrgBootstrap } from "@dreambigger/shared/src/types/organization";
import useSWR from "swr";
import { storyPaths } from "@dreambigger/shared/src/api/stories";
import {
  Head,
  Group,
  Avatar,
  Typography,
  Button,
  Divider,
  Input,
  Form,
  FacebookShareButton,
  TwitterShareButton,
  EmailShareButton,
  CopyToClipboard,
  Comment as AntComment,
  Popover,
  Modal,
  Spin,
  Tooltip,
  Popconfirm,
  message,
} from "@dreambigger/design-system";
import cx from "classnames";
import { Icon, IconLookup } from "@dreambigger/design-system/src/icons";
import { LikeOutlined, LikeFilled } from "@dreambigger/design-system/src/icons";
import {
  faEllipsisV,
  faFlag,
  faTimes,
  faTrash,
  faShareAlt,
  faEnvelope,
} from "@dreambigger/design-system/src/icons/light";
import { faFacebookSquare } from "@dreambigger/design-system/src/icons/solid";
import { XOutlined } from "@ant-design/icons";
import {
  generateCloudinaryUrl,
  optimizeCloudinaryImg,
} from "@dreambigger/shared/src/utils";
import { calculateTimePast } from "@dreambigger/shared/src/dates/calculate-days-past";
import Comment from "../comment/story-comment";
import { communityPaths } from "@dreambigger/shared/src/api/communities";
import { Community } from "@dreambigger/shared/src/types";
import useUser from "@dreambigger/shared/src/hooks/use-user";
import Gallery from "../gallery/gallery";
import cotributeDefaults from "@dreambigger/shared/src/assets/cotribute-defaults";
import {
  api,
  tokenHelper,
  communityApi,
  orgApi,
  commentApi,
  storyApi,
} from "@dreambigger/shared/src/api/weserve/api-helper";
import { useTryAsync } from "no-try";
import { useRouter } from "next/router";
import InspireAnalytics, {
  InspireAnalyticsHelper,
  InspireAnalyticEvents,
} from "@dreambigger/shared/src/utils/inspire-analytics-helper";
import {
  useGA,
  useMixpanel,
  useAppcues,
  useSegment,
} from "@dreambigger/shared/src/hooks";

const fetcherFn = (url: string) => fetcher(api, tokenHelper, url);
const { Paragraph, Title, Text } = Typography;
const { TextArea } = Input;

type StoryProps = {
  orgBootstrap: OrgBootstrap;
  story?: StoryType;
  community?: Community;
};

const Story = ({
  orgBootstrap: initialOrgBootstrap,
  story: initialStory,
  community: initialCommunityData,
}: StoryProps) => {
  const { isFallback } = useRouter();
  const [storyMenuLoading, setStoryMenuLoading] = useState(false);
  const [addingComment, setAddingComment] = useState(false);
  const [togglingLike, setTogglingLike] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [commentForm] = Form.useForm();
  const router = useRouter();

  // Load analytics hooks
  const mixpanel = useMixpanel();
  const segment = useSegment();
  const googleAnalytics = useGA();

  // Load appcues hook
  const appcues = useAppcues();

  const [userLoading, { data: me }] = useUser(api, tokenHelper);

  // NOTE: A Story should always be guaranteed at least one image according to backend logic. Either user uploaded or a standard background image.
  const getImage = (image?: StoryImage) => {
    if (!image) {
      return "";
    }

    if (image.meta?.src) {
      return optimizeCloudinaryImg(image.meta.src);
    }

    if (image.imageVersion && image.imageId) {
      return generateCloudinaryUrl(image.imageVersion, image.imageId);
    }

    return "";
  };

  const showModal = () => {
    setIsModalVisible(true);
    // Modal - Opened (legacy parity)
    googleAnalytics.event({
      category: "Modal - Opened",
      action: "Select",
      label: "Story Share Modal",
    });
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    // Modal - Closed (legacy parity)
    googleAnalytics.event({
      category: "Modal - Closed",
      action: "Select",
      label: "Story Share Modal",
    });
  };

  const { data: storyData, mutate: refetchStory } = useSWR<StoryType, any>(
    initialStory?.id ? storyPaths.getById(initialStory.id) : null,
    fetcherFn,
    {
      fallbackData: initialStory,
      onError: (error) => {
        console.error(error);
        message.error("Something went wrong loading this story.");
      },
    }
  );

  const { data: communityData } = useSWR<Community, any>(
    initialStory?.organization.slug
      ? communityPaths.getBySlug(initialStory.organization.slug)
      : null,
    fetcherFn,
    {
      fallbackData: initialCommunityData,
    }
  );

  const story = storyData || initialStory;

  // HANDLECLICK CALLBACK FUNCTIONS --------------
  const handleStoryLike = useCallback(() => {
    if (!me) return;
    const isAddingLike = !story?.iLiked;
    setTogglingLike(true);
    storyApi
      .toggleStoryLike(story!.id)
      .then(() =>
        refetchStory().finally(() => {
          setTogglingLike(false);
        })
      )
      .catch(() => setTogglingLike(false));

    // Track the like in analytics
    googleAnalytics.event({
      category: "Like",
      action: "Selected",
      label: "Type:Story-View",
    });
    InspireAnalytics.track({
      eventName: InspireAnalyticEvents.LIKE,
      properties: {
        "Card Type": "Story",
        "Page Type": InspireAnalyticsHelper.getPageType(router.route),
      },
      mixpanel,
      segment,
    });

    // People aggregates fired only when adding a like.
    //
    // BEHAVIOR CHANGE vs. legacy: `app/js/directives/luvv-it.js` called
    // `sendMxpnl()` from both the like and the unlike branch (lines 58-70),
    // so `Last Like TS` got re-stamped and `zzBMVP1: Total Likes` got
    // incremented on every click — including unlikes. We deliberately gate
    // on `isAddingLike` here because the legacy double-fire produced a
    // counter that over-counts toggles rather than unique likes, which was
    // probably never the intent. If strict legacy parity is needed for a
    // downstream cohort, drop the `if (isAddingLike)` guard.
    //
    // Sources differ by property:
    //   - "Last Like TS" + "zzBMVP1: Total Likes" come from legacy
    //     `app/js/directives/luvv-it.js`.
    //   - "# of Likes" comes from legacy
    //     `platform/components/iconLuvvit/index.js` (different legacy
    //     product surface; not present in `app/`). Kept here so cohorts
    //     built on the platform-side property continue to see new likes.
    if (isAddingLike) {
      const nowTs = +new Date();
      InspireAnalytics.setPeopleProperties({
        mixpanel,
        properties: { "Last Like TS": nowTs },
      });
      InspireAnalytics.incrementPeopleProperty({
        mixpanel,
        property: "# of Likes",
      });
      InspireAnalytics.incrementPeopleProperty({
        mixpanel,
        property: "zzBMVP1: Total Likes",
      });
    }
  }, [story, me]);

  const handleAddComment = useCallback(
    (values: any) => {
      if (!me || !values["comment_text"] || addingComment) return;

      setAddingComment(true);

      const commentText = values["comment_text"];
      commentApi
        .postCommentToStory(story!.id, commentText)
        .then((res: any) => {
          // Commenter tracking. Property names match legacy; event payload
          // schema deliberately diverges:
          //   - People properties "Last Comment TS", "zzBMVP1: Total
          //     Comments", and "zzBMVP1: Total Mentions" use the exact
          //     strings from legacy `app/js/controllers/commentsctrl.js`.
          //   - The "Comment" Mixpanel event payload here uses
          //     Inspire-relevant fields (Description, Comment Id, Story ID,
          //     Page Type, Mention) instead of the legacy schema (Type,
          //     Categories, Organization Name, Campaign Name, Mention,
          //     Mention Team Member) — those fields don't map cleanly to the
          //     Inspire surface, so this is an intentional schema migration
          //     for the new product context.
          //   - "# of Comments" comes from legacy
          //     `platform/components/commentReplyBox/index.js` and
          //     `platform/components/storiesPost/index.js` (different legacy
          //     product surface; not present in `app/`). Kept here so cohorts
          //     built on the platform-side property continue to see new
          //     comments.
          // GA was already firing "Comment on Post" via the existing handler
          // so we intentionally don't duplicate that here.
          const commentTimestamp = res?.data?.timestamp || +new Date();
          const mentionMatches = commentText.match(/@/g) || [];
          InspireAnalytics.track({
            eventName: InspireAnalyticEvents.COMMENT,
            properties: {
              Description: commentText,
              "Comment Id": res?.data?.id,
              "Story ID": story!.id,
              "Page Type": InspireAnalyticsHelper.getPageType(router.route),
              Mention: mentionMatches.length > 0 ? "Yes" : "No",
            },
            mixpanel,
            segment,
          });
          InspireAnalytics.incrementPeopleProperty({
            mixpanel,
            property: "# of Comments",
          });
          InspireAnalytics.setPeopleProperties({
            mixpanel,
            properties: { "Last Comment TS": commentTimestamp },
          });
          InspireAnalytics.incrementPeopleProperty({
            mixpanel,
            property: "zzBMVP1: Total Comments",
          });
          if (mentionMatches.length > 0) {
            InspireAnalytics.incrementPeopleProperty({
              mixpanel,
              property: "zzBMVP1: Total Mentions",
              value: mentionMatches.length,
            });
          }

          return refetchStory().finally(() => {
            setAddingComment(false);
            commentForm.resetFields();
          });
        })
        .catch(() => setAddingComment(false));
    },
    [story, me]
  );

  const handleReport = useCallback(() => {
    setStoryMenuLoading(true);

    storyApi
      .reportStory(story!.id)
      .then(() =>
        refetchStory().finally(() => {
          window.location.reload();
          setStoryMenuLoading(false);
        })
      )
      .catch(() => setStoryMenuLoading(false));
  }, [story]);

  const handleDelete = useCallback(() => {
    setStoryMenuLoading(true);

    storyApi
      .deleteStory(story!.id)
      .then(() =>
        refetchStory().finally(() => {
          window.location.reload();
          setStoryMenuLoading(false);
        })
      )
      .catch(() => setStoryMenuLoading(false));
  }, [story]);

  // GUARD CLAUSES -----------------------
  if (isFallback) {
    // This page has not been statically rendered and needs to be generated by the server.
    return <Spin style={{ width: "100%", marginTop: 150 }} />;
  }

  if (!story) {
    // If we ever get here and don't have a story then there was an error with server side rendering
    // the page.
    message.error("Error loading page");
    return null;
  }

  // VARIABLES AND METADATA ----------------
  const community = communityData || initialCommunityData;
  const authorInitials = (
    (story.user?.firstName[0] ?? "") + (story.user?.lastName[0] ?? "")
  ).toUpperCase();

  const meInitials = (
    (me?.firstName[0] ?? "") + (me?.lastName[0] ?? "")
  ).toUpperCase();

  const hasStoryUserImage =
    story.user?.imageId &&
    story.user?.imageVersion &&
    // We don't want to use the default avatar image that is saved to users.
    story.user?.imageId !== "avatar";

  const hasMyUserImage =
    me?.imageId &&
    me?.imageVersion &&
    // We don't want to use the default avatar image that is saved to users.
    me?.imageId !== "avatar";
  const bannerImage = community?.meta?.footerBanner?.image;
  const showCommentBanner = community?.meta?.footerBanner?.title || bannerImage;
  const hasImages = (story?.images && story?.images.length > 0) || undefined;
  const images = (hasImages && story.images!.map((img) => getImage(img))) || [];

  const socialUrl = process?.browser ? window.location.href : "";

  const tabIcon =
    community?.meta?.brand?.tabIcon || cotributeDefaults.tabIconIco;

  // Meta data
  const headMeta = {
    favicon: tabIcon,
    title: story.description,
    description: "Check out how much fun we had making a difference together!",
    siteName: "Stories",
    image: images[0],
  };

  // ANALYTICS
  /// Track the page view for analytics and appcues
  useEffect(() => {
    // Give GoogleAnalytics time to initialize
    setTimeout(() => {
      // Wait for a response on whether a user is logged in.
      if (userLoading) {
        return;
      }
      googleAnalytics.setDefaultDimensions(community?.slug, me);
      googleAnalytics.pageview(window.location.pathname);
      InspireAnalytics.track({
        eventName: InspireAnalyticEvents.PAGE_VIEW,
        properties: {
          Page: window.location.pathname,
          "Page Type": InspireAnalyticsHelper.getPageType(router.route),
          "Host Name": window.location.host,
        },
        mixpanel,
        segment,
      });
      // If there is a user, track user page visit on appcues
      if (me?.id) {
        appcues.identify(me.id.toString());
      }
    }, 500);
  }, [userLoading]);

  // **************************************************************
  // *                         RENDER                             *
  // **************************************************************
  return (
    <>
      <Head {...headMeta} />
      <Group lg="horizontal" className="h-100 justify-center overflow-y-hidden">
        {/* GALLERY (left side of screen on desktop)*/}
        {hasImages && <Gallery images={images} />}

        {/* SIDEBAR (right of screen on desktop) */}
        <Group
          style={{ minWidth: 320 }}
          className={cx(
            hasImages
              ? "lg_max-w-50 overflow-y-auto"
              : "lg_ba lg_br-2 lg_b-gray-5 lg_mv-6",
            "bg-white lg_w-sidebar w-100"
          )}
        >
          <>
            {/* AVATAR AND POST DATE -------------------------------------------------- */}
            <Group className="ph-5 pv-6" spacing={5}>
              {/* --- Avatar */}
              <Group spacing={3} xs="horizontal" className="items-center">
                {story.user && (
                  <Avatar
                    size="large"
                    alt="story user avatar"
                    src={
                      hasStoryUserImage &&
                      generateCloudinaryUrl(
                        story.user.imageVersion,
                        story.user.imageId,
                        { type: "avatar" }
                      )
                    }
                  >
                    {!hasStoryUserImage && authorInitials}
                  </Avatar>
                )}
                <Group>
                  {/* ----- Username */}
                  {story.user && (
                    <Title level={5}>
                      {story.user.firstName + " " + story.user.lastName}
                    </Title>
                  )}
                  {/* ----- Post Date */}
                  <Text className="f-2 gray-8">
                    {calculateTimePast(new Date(story.timestamp))}
                  </Text>
                </Group>
              </Group>
              {/* DESCRIPTION ------------------------------------------------------- */}
              <Group spacing={3}>
                <Paragraph className="f-4">{story.description}</Paragraph>
                {/* Associated Story Name/Link - if none, show un-linked community organization name instead */}
                <Group xs="horizontal" spacing={2} className="items-center">
                  <Divider className="min-w-5" style={{ width: 30 }} />
                  {story?.event?.id ? (
                    // TODO: Enable Link once fundraiser events are on Dreambigger. <Link href={`/events/${story.event.id}/detail`}>
                    <a href={`/events/${story.event.id}/detail`}>
                      <Text className="primary underline pointer">
                        {story.event.name ||
                          `Click to view associated ${
                            community?.meta?.marketing?.keyword || "event"
                          }`}
                      </Text>
                    </a>
                  ) : (
                    // </Link>
                    <Text className="gray-8">{story.organization.name}</Text>
                  )}
                </Group>
              </Group>

              {/* USER ACTIONS BAR ------------------------------------------------- */}
              <Group xs="horizontal" wrap spacing={2}>
                {/* Action Bar SHARE BUTTON - Launches modal */}
                <Button
                  type="primary"
                  onClick={showModal}
                  className="lightenPrimaryBkd"
                  aria-label="open share modal"
                >
                  <Icon icon={faShareAlt as IconLookup} className="mr-2" />
                  SHARE
                </Button>

                {/* --- Share Modal Content */}
                <Modal
                  title={<Title level={5}>Share Post</Title>}
                  open={isModalVisible}
                  onCancel={handleCancel}
                  className="max-w-100"
                  footer={null}
                  closeIcon={<Icon icon={faTimes as IconLookup} />}
                >
                  <Group
                    xs="horizontal"
                    className="justify-between pv-4 sm_ph-5"
                    spacing={1}
                  >
                    {/* ----- Facebook */}
                    <Group className="items-center tc" spacing={3}>
                      <FacebookShareButton
                        aria-label="share on facebook"
                        url={socialUrl}
                        onClick={handleCancel}
                      >
                        <Icon
                          icon={faFacebookSquare as IconLookup}
                          className="w-7 h-7 sm_w-8 sm_h-8"
                          style={{ color: "#1778F2" }}
                        />
                      </FacebookShareButton>
                      <Text className="f-2">Facebook</Text>
                    </Group>

                    {/* ----- X (Twitter) */}
                    <Group className="items-center tc" spacing={3}>
                      <div
                        className="flex justify-center"
                        style={{ width: "48px", height: "48px" }}
                      >
                        <TwitterShareButton
                          aria-label="share on X"
                          url={socialUrl}
                          onClick={handleCancel}
                        >
                          <XOutlined
                            style={{ color: "#000000", fontSize: "28px" }}
                          />
                        </TwitterShareButton>
                      </div>
                      <Text className="f-2">X</Text>
                    </Group>

                    {/* ----- Email */}
                    <Group className="items-center tc" spacing={3}>
                      <EmailShareButton
                        aria-label="share in email"
                        url={socialUrl}
                        beforeOnClick={handleCancel}
                      >
                        <Icon
                          icon={faEnvelope as IconLookup}
                          className="w-7 h-7 sm_w-8 sm_h-8 gray-7"
                        />
                      </EmailShareButton>
                      <Text className="f-2">Email</Text>
                    </Group>

                    {/* ----- Clipboard */}
                    <Group className="items-center tc" spacing={3}>
                      <CopyToClipboard
                        text={socialUrl}
                        onClick={handleCancel}
                        className="h-7 sm_h-8"
                        iconClassName="w-7 h-7 sm_w-8 sm_h-8 pv-1 gray-7"
                      />
                      <Text className="f-2">Copy Link</Text>
                    </Group>
                  </Group>
                </Modal>

                {/* Action Bar LIKE BUTTON */}
                <Tooltip title={!me ? "Sign in to like" : ""}>
                  <Button
                    className={story.iLiked ? "b-primary" : ""}
                    loading={togglingLike}
                    onClick={handleStoryLike}
                    disabled={!me}
                  >
                    {story.iLiked ? (
                      <LikeFilled className="primary" />
                    ) : (
                      <LikeOutlined />
                    )}
                    <span className={cx(story.iLiked ? "primary" : "", "ml-2")}>
                      {story.likes}
                    </span>
                  </Button>
                </Tooltip>

                {/* Action Bar OTHER BUTTON - Is wrapped in a popover that gives a user the option to delete a story. */}
                <Popover
                  content={
                    me ? (
                      <Popconfirm
                        title="Are you sure you want to delete this story?"
                        onConfirm={handleDelete}
                      >
                        <Button
                          className="hlight-primary"
                          icon={
                            <Icon
                              icon={faTrash as IconLookup}
                              className="mr-2"
                            />
                          }
                        >
                          Delete Post
                        </Button>
                      </Popconfirm>
                    ) : (
                      <Popconfirm
                        title="Are you sure you want to report this story?"
                        onConfirm={handleReport}
                      >
                        <Button
                          className="hlight-primary"
                          icon={
                            <Icon
                              icon={faFlag as IconLookup}
                              className="mr-2"
                            />
                          }
                        >
                          Report Post
                        </Button>
                      </Popconfirm>
                    )
                  }
                  trigger="click"
                  placement="bottomRight"
                >
                  <Button
                    loading={storyMenuLoading}
                    icon={<Icon icon={faEllipsisV as IconLookup} />}
                  />
                </Popover>
              </Group>
            </Group>
            <Divider />
            {/* Comments ------------------------------------------------- */}
            {userLoading ? (
              <Spin style={{ textAlign: "left", margin: 24 }} />
            ) : (
              <Group className="ph-5 pv-4">
                {/* Display comments and comment input if community allows */}
                {community?.organization.meta.showInspireComments && (
                  <>
                    {me && (
                      <>
                        <AntComment
                          avatar={
                            <Avatar
                              alt="logged in user avatar"
                              src={
                                hasMyUserImage &&
                                generateCloudinaryUrl(
                                  me.imageVersion!,
                                  me.imageId!,
                                  { type: "avatar" }
                                )
                              }
                            >
                              {!hasMyUserImage && meInitials}
                            </Avatar>
                          }
                          content={
                            <>
                              <Form
                                form={commentForm}
                                onFinish={handleAddComment}
                              >
                                <Form.Item name="comment_text">
                                  <TextArea rows={4} />
                                </Form.Item>
                                <Form.Item>
                                  <Button
                                    htmlType="submit"
                                    type="primary"
                                    loading={addingComment}
                                  >
                                    ADD COMMENT
                                  </Button>
                                </Form.Item>
                              </Form>
                            </>
                          }
                        />
                        {story.comments?.records &&
                          story.comments.records?.length > 0 && (
                            <Divider className="mt--2 mb-4" />
                          )}
                      </>
                    )}
                    {story.comments &&
                      story.comments.totalRecords > 0 &&
                      story.comments.records
                        ?.map((comment) => (
                          <Comment
                            refetchStory={refetchStory}
                            currentUser={me}
                            comment={comment}
                            key={comment.id}
                          />
                        ))
                        .reverse()}
                  </>
                )}

                {/* COMMENT BANNER/CTA ------------------------------------ */}
                {showCommentBanner && (
                  <Group
                    className={cx(
                      bannerImage ? "tl" : "tc",
                      "br-4 bg-cta pa-5 mv-4 ml--2 mr--2 sm_mh-0"
                    )}
                  >
                    <Group spacing={5} sm="horizontal" className="items-start">
                      {bannerImage && (
                        <img
                          className="sm_w-40 w-100"
                          alt="community banner image"
                          style={{ objectFit: "contain" }}
                          src={optimizeCloudinaryImg(bannerImage)}
                        />
                      )}
                      <Group spacing={4}>
                        <Text className="textSecondary f-4">
                          {community?.meta?.footerBanner?.title}
                        </Text>
                        <span>
                          <Button
                            type="primary"
                            onClick={() => {
                              const action =
                                community?.meta?.footerBanner?.ctaAction;
                              const openInNewTab =
                                community?.meta?.footerBanner?.target ===
                                "_blank";
                              if (openInNewTab) {
                                window.open(action, "_blank");
                                return;
                              }
                              window.open(action);
                            }}
                          >
                            {community?.meta?.footerBanner?.ctaTitle}
                          </Button>
                        </span>
                      </Group>
                    </Group>
                  </Group>
                )}
              </Group>
            )}
          </>
        </Group>
      </Group>
    </>
  );
};

// STATIC GENERATION
export const getStoryStaticPaths: GetStaticPaths = async () => {
  const res = await api.get("/active-story-ids");
  const storyIds: number[] = res.data.storyIds;

  const blackListedStoryIds = (
    process.env.BLACKLISTED_STORY_IDS || "404"
  ).split(",");

  // Only statically generate the last X amount of stories. Default to 300
  const staticPageLimit = process.env.ENTITY_STATIC_PAGE_LIMIT || 300;

  return {
    paths: storyIds
      // Only statically generate the last 300 stories.
      .slice(0, Math.min(Number(staticPageLimit), storyIds.length))
      .filter((id) => !blackListedStoryIds.includes(String(id)))
      .map((id) => ({ params: { storyId: String(id) } })),
    fallback: true,
  };
};

type ReturnProps = {
  story: StoryType;
  orgBootstrap: OrgBootstrap;
  community?: Community;
};

export const getStoryStaticProps: GetStaticProps = async ({ params }) => {
  const storyId = params!.storyId as string;

  const [storyError, storyRes] = await useTryAsync(() =>
    storyApi.getById(Number(storyId))
  );

  if (storyError) {
    console.error("Unable to load story:", storyError);
    return {
      notFound: true,
    };
  }

  const story: StoryType = storyRes?.data;

  const orgBootstrapRes = await orgApi.getOrgBootstrap(story.organization.id);
  const orgBootstrap = orgBootstrapRes.data;

  const returnProps = {
    story,
    orgBootstrap,
  } as ReturnProps;

  const [communityError, communityResponse] = await useTryAsync(() =>
    communityApi.getBySlug(orgBootstrap?.slug)
  );

  if (!communityError && communityResponse?.data) {
    returnProps.community = communityResponse.data;
  }

  return {
    props: returnProps,
    revalidate: 120,
  };
};

export default Story;
